import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api'
import { useAuth } from '../auth'
import CodeOrderTree, { allPallets } from './CodeOrderTree'
import SupplyBuilder from './SupplyBuilder'
import { formatDate } from '../lib/format'
import { useEscClose } from '../lib/useEscClose'
import { PALLET_STATUS_LABEL, PALLET_STATUS_ORDER } from '../types'
import type { CodeOrderDetail, PalletStatus } from '../types'

/**
 * Карточка заказа кодов.
 *
 * Собрана вокруг того, что делают, а не вокруг того, что известно. Раньше в
 * правой колонке стояли рядом шесть блоков — справочник, номера упаковок,
 * выгрузки, реквизиты, разбор, расхождения, — и каждый требовал внимания
 * одинаково. Разобраться в этом было нельзя.
 *
 * Теперь так: наверху — где поставка и что с ней не так; посередине — панель
 * действий, работающая по выделению; справа — только то, что просит внимания
 * сейчас, а настройка убрана в свёрнутый раздел, куда заходят раз в месяц.
 */
export default function CodeOrderCard({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient()
  const { can } = useAuth()
  const mayEdit = can('tickets.reply')

  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [выделено, setВыделено] = useState<Set<number>>(new Set())
  const [гтд, setГтд] = useState<{ truckId?: number; palletId?: number } | null>(null)
  const [итог, setИтог] = useState<string[]>([])
  const [собираем, setСобираем] = useState(false)

  useEscClose(onClose)

  const order = useQuery({ queryKey: ['code-order', id], queryFn: () => api.codeOrder(id) })
  // Справочник нужен конструктору поставки: товар выбирают из карточек, а не
  // вписывают названием.
  const товары = useQuery({
    queryKey: ['code-order', id, 'products'],
    queryFn: () => api.codeOrderProducts(id),
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['code-order', id] })
    qc.invalidateQueries({ queryKey: ['code-orders'] })
  }

  async function действие<T>(что: () => Promise<T>, итоги?: (r: T) => string[]) {
    setBusy(true)
    setError('')
    try {
      const r = await что()
      if (итоги) setИтог(итоги(r))
      refresh()
    } catch (e: any) {
      setError(e?.message ?? 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  const data = order.data
  const паллеты = useMemo(() => (data ? allPallets(data) : []), [data])
  const выбранные = useMemo(
    () => паллеты.filter((p) => выделено.has(p.id)),
    [паллеты, выделено],
  )

  // Позиции, затронутые выделением: массовая разбивка идёт по товарам,
  // а выделяют паллеты — переводим одно в другое.
  const выбранные_позиции = useMemo(() => {
    if (!data) return []
    const ids = new Set<number>()
    for (const t of data.trucks)
      for (const i of t.items)
        if (i.pallets.some((p) => выделено.has(p.id))) ids.add(i.id)
    return [...ids]
  }, [data, выделено])

  function выделить(ids: number[], on: boolean) {
    setВыделено((было) => {
      const стало = new Set(было)
      ids.forEach((x) => (on ? стало.add(x) : стало.delete(x)))
      return стало
    })
  }

  return (
    <>
      <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal giant cow">
          <Head data={data} id={id} onClose={onClose} />

          {error && (
            <div className="error" style={{ margin: '10px 20px 0' }}>
              {error}
            </div>
          )}
          {итог.length > 0 && (
            <div className="cow-result">
              {итог.map((с, i) => (
                <div key={i}>{с}</div>
              ))}
              <button className="link-btn" onClick={() => setИтог([])}>
                скрыть
              </button>
            </div>
          )}
          {order.isLoading && <p className="empty">Загрузка…</p>}

          {data && (
            <>
              <Steps data={data} />

              <div className="cow-body">
                <div className="cow-main">
                  {mayEdit && data.trucks.length > 0 && (
                    <Toolbar
                      data={data}
                      busy={busy}
                      выбрано={выбранные.length}
                      всего={паллеты.length}
                      onAll={(on) => выделить(паллеты.map((p) => p.id), on)}
                      onSpread={(files) =>
                        void действие(
                          () =>
                            api.spreadCodes(
                              id,
                              files,
                              выбранные.length ? [...выделено] : undefined,
                            ),
                          (r) => [
                            ...r.placed.map(
                              (x) => `✓ ${x.product}: ${x.codes.toLocaleString('ru')} кодов`,
                            ),
                            ...r.unknown.map(
                              (x) => `⚠ ${x.file}: товар ${x.gtin ?? '?'} в поставке не найден`,
                            ),
                            ...r.rejected.map((x) => `✗ ${x.product}: ${x.reason}`),
                            ...r.notes,
                          ],
                        )
                      }
                      onSplit={() =>
                        void действие(
                          () => api.splitMany(id, выбранные_позиции),
                          (r) => [
                            ...r.done.map((x) => `✓ ${x.product}: ${x.pallets} паллет`),
                            ...r.rejected.map((x) => `✗ ${x.product}: ${x.reason}`),
                          ],
                        )
                      }
                      onStatus={(s) =>
                        void действие(() =>
                          api.setPalletStatus(id, [...выделено], s),
                        )
                      }
                      onExport={(что) => {
                        const ids = выбранные.length ? [...выделено] : undefined
                        const хвост = выбранные.length ? ` (${выбранные.length})` : ''
                        const упало = (e: any) =>
                          setError(e?.message ?? 'Не удалось выгрузить')
                        if (что === 'pallets' || что === 'codes') {
                          const имя =
                            что === 'pallets'
                              ? `Палеты и SSCC — ${data.title}${хвост}.xlsx`
                              : `Коды маркировки — ${data.title}${хвост}.xlsx`
                          void api.downloadCodeOrderReport(id, что, имя, ids).catch(упало)
                        } else {
                          void api
                            .downloadBundle(id, `${data.title}${хвост}.zip`, ids, что)
                            .catch(упало)
                        }
                      }}
                    />
                  )}

                  <CodeOrderTree
                    order={data}
                    selected={mayEdit ? выделено : undefined}
                    onSelect={mayEdit ? выделить : undefined}
                    onDownload={(fileId, name) =>
                      void api
                        .downloadCodeOrderFile(id, fileId, name)
                        .catch((e: any) => setError(e?.message ?? 'Не удалось скачать'))
                    }
                    actions={
                      mayEdit
                        ? {
                            busy,
                            onSource: (f) =>
                              void действие(() => api.uploadCodeOrderSource(id, f)),
                            onBuild: () => setСобираем(true),
                            onCzOrder: (truckId, name) =>
                              void api
                                .downloadCzOrder(id, truckId, name)
                                .catch((e: any) =>
                                  setError(e?.message ?? 'Не удалось собрать файл'),
                                ),
                            onCodes: (itemId, files) =>
                              void действие(() => api.uploadCodes(id, itemId, files)),
                            onSplit: (itemIds) =>
                              void действие(
                                () => api.splitMany(id, itemIds),
                                (r) => [
                                  ...r.done.map((x) => `✓ ${x.product}: ${x.pallets} паллет`),
                                  ...r.rejected.map((x) => `✗ ${x.product}: ${x.reason}`),
                                ],
                              ),
                            onGtd: (where) => setГтд(where),
                          }
                        : undefined
                    }
                  />
                </div>

                <Aside
                  data={data}
                  id={id}
                  mayEdit={mayEdit}
                  busy={busy}
                  onError={setError}
                  onChanged={refresh}
                  onBuild={() => setСобираем(true)}
                  действие={действие}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {собираем && data && (
        <SupplyBuilder
          order={data}
          products={товары.data ?? []}
          onPreview={(body) => api.previewCodeOrderBuild(id, body)}
          onSave={async (body) => {
            const r = await api.buildCodeOrder(id, body)
            refresh()
            return r
          }}
          onClose={() => setСобираем(false)}
        />
      )}

      {гтд && (
        <GtdDialog
          onClose={() => setГтд(null)}
          onSend={(file, реквизиты) => {
            setГтд(null)
            void действие(() => api.uploadGtd(id, file, гтд, реквизиты))
          }}
        />
      )}
    </>
  )
}

/** Шапка: номер, этап и числа поставки одной строкой. */
function Head({
  data,
  id,
  onClose,
}: {
  data: CodeOrderDetail | undefined
  id: number
  onClose: () => void
}) {
  return (
    <div className="tkw-head">
      <div className="tkw-titles">
        <div className="tkw-num">
          {data?.number ? `Поставка № ${data.number}` : `Заказ кодов № ${id}`}
          {data?.supply_date && (
            <span className="tkw-date">от {formatDate(data.supply_date)}</span>
          )}
          {data && (
            <>
              <span className={`co-chip co-${data.stage}`}>{data.stage_title}</span>
              {data.from_client && <span className="tk-cat-chip">из кабинета</span>}
            </>
          )}
        </div>
        {data && (
          <>
            <div className="tkw-subject">
              {data.title}
              <span className="cot-client"> · {data.client_name}</span>
            </div>
            <div className="cow-totals">
              <span>
                <b>{data.trucks_count}</b> грузовиков
              </span>
              <span>
                <b>{data.items_count}</b> позиций
              </span>
              <span>
                <b>{data.pallets_count}</b> паллет
              </span>
              <span>
                <b>{data.units_total.toLocaleString('ru')}</b> шт
              </span>
            </div>
          </>
        )}
      </div>
      <button className="tkw-close" title="Закрыть окно" onClick={onClose}>
        ✕
      </button>
    </div>
  )
}

/** Лента этапов — поперёк окна, а не списком сбоку. */
function Steps({ data }: { data: CodeOrderDetail }) {
  return (
    <div className="cow-steps">
      {data.steps.map((s) => (
        <span
          key={s.stage}
          className={`cow-step${s.current ? ' current' : s.done ? ' done' : ''}`}
        >
          {s.title}
        </span>
      ))}
    </div>
  )
}

/**
 * Панель массовых действий.
 *
 * Всегда над деревом и всегда говорит, сколько выделено: действие на
 * пятьсот паллет не должно случаться незаметно.
 */
function Toolbar({
  data,
  busy,
  выбрано,
  всего,
  onAll,
  onSpread,
  onSplit,
  onStatus,
  onExport,
}: {
  data: CodeOrderDetail
  busy: boolean
  выбрано: number
  всего: number
  onAll: (on: boolean) => void
  onSpread: (files: File[]) => void
  onSplit: () => void
  onStatus: (s: PalletStatus) => void
  onExport: (что: 'all' | 'csv' | 'xml' | 'pallets' | 'codes') => void
}) {
  const [статус, setСтатус] = useState<PalletStatus | ''>('')

  return (
    <div className="cow-bar">
      <label
        className="btn small"
        style={{ cursor: 'pointer' }}
        title={
          выбрано > 0
            ? 'Коды лягут только в выделенные позиции — по GTIN внутри файлов'
            : 'Файлы разложатся по всей поставке сами — по GTIN внутри кодов'
        }
      >
        ⬇ Загрузить коды пачкой{выбрано > 0 ? ' в выбранное' : ''}
        <input
          type="file"
          accept=".csv,.txt"
          multiple
          style={{ display: 'none' }}
          disabled={busy}
          onChange={(e) => {
            const список = Array.from(e.target.files ?? [])
            if (список.length) onSpread(список)
            e.target.value = ''
          }}
        />
      </label>

      <span className="cow-bar-sep" />

      <label className="cow-bar-all">
        <input
          type="checkbox"
          checked={выбрано > 0 && выбрано === всего}
          ref={(el) => {
            if (el) el.indeterminate = выбрано > 0 && выбрано < всего
          }}
          onChange={(e) => onAll(e.target.checked)}
        />
        {выбрано > 0 ? (
          <span>
            выбрано <b>{выбрано}</b> из {всего}
          </span>
        ) : (
          <span>выбрать все {всего}</span>
        )}
      </label>

      <button className="btn small" disabled={busy || выбрано === 0} onClick={onSplit}>
        ✂ Разбить выбранные
      </button>

      <select
        className="btn small"
        value={статус}
        disabled={busy || выбрано === 0}
        onChange={(e) => {
          const s = e.target.value as PalletStatus
          setСтатус('')
          if (s) onStatus(s)
        }}
      >
        <option value="">Перевести в…</option>
        {PALLET_STATUS_ORDER.filter((s) => s !== 'none').map((s) => (
          <option key={s} value={s}>
            {PALLET_STATUS_LABEL[s]}
          </option>
        ))}
      </select>

      {/* Выгрузки идут по выделению: ничего не выделено — по всей поставке.
          Четыре кнопки вместо одной, потому что забирают по-разному: кому
          архив целиком, кому только коды, кому таблицу для бухгалтерии. */}
      <span className="cow-exports">
        <span className="cow-exports-label">
          {выбрано > 0 ? `выгрузить выбранные (${выбрано}):` : 'выгрузить всё:'}
        </span>
        <button className="btn tiny" disabled={busy} onClick={() => onExport('all')}>
          🗜 архив
        </button>
        <button className="btn tiny" disabled={busy} onClick={() => onExport('csv')}>
          csv
        </button>
        <button className="btn tiny" disabled={busy} onClick={() => onExport('xml')}>
          xml
        </button>
        <button className="btn tiny" disabled={busy} onClick={() => onExport('pallets')}>
          📊 паллеты
        </button>
        <button className="btn tiny" disabled={busy} onClick={() => onExport('codes')}>
          📊 коды
        </button>
      </span>

      <div style={{ flex: 1 }} />

      <span className="cow-counts">
        {PALLET_STATUS_ORDER.filter((s) => data.status_counts[s]).map((s) => (
          <span key={s} className={`pal-chip pal-${s}`}>
            {PALLET_STATUS_LABEL[s]}: {data.status_counts[s]}
          </span>
        ))}
      </span>
    </div>
  )
}

/** Правая колонка: только то, что просит внимания. Настройка — свёрнута. */
function Aside({
  data,
  id,
  mayEdit,
  busy,
  onError,
  onChanged,
  onBuild,
  действие,
}: {
  data: CodeOrderDetail
  id: number
  mayEdit: boolean
  busy: boolean
  onError: (e: string) => void
  onChanged: () => void
  onBuild: () => void
  действие: <T>(что: () => Promise<T>, итоги?: (r: T) => string[]) => Promise<void>
}) {
  const [раскрыт, setРаскрыт] = useState<string | null>(null)
  const горит = data.expiring.filter((e) => e.days_left <= 7).length

  return (
    <div className="cow-aside">
      {data.unmatched.length > 0 && (
        <Section title={`Не узнаны товары (${data.unmatched.length})`} tone="warn" open>
          <Nomenclature
            data={data}
            id={id}
            mayEdit={mayEdit}
            busy={busy}
            onError={onError}
            onChanged={onChanged}
          />
        </Section>
      )}

      {data.stage === 'parsed' && mayEdit && (
        <Section title="Разбор ждёт подтверждения" tone="warn" open>
          <p className="hint" style={{ marginTop: 0 }}>
            Сверьте дерево {data.files.length ? 'с файлом' : 'со своими бумагами'}.
            После подтверждения по нему заказываются коды, и переделывать будет
            поздно.
          </p>
          <button
            className="btn primary"
            disabled={busy}
            onClick={() => void действие(() => api.confirmCodeOrder(id))}
          >
            Разложено верно
          </button>
        </Section>
      )}

      {data.notes.length > 0 && (
        <Section
          title={`Расхождения в файле (${data.notes.length})`}
          tone="warn"
          open={раскрыт === 'notes'}
          onToggle={() => setРаскрыт(раскрыт === 'notes' ? null : 'notes')}
        >
          <p className="hint" style={{ marginTop: 0 }}>
            Программа ничего не исправляла сама: неизвестно, какое из чисел
            верное, а молчаливая правка в маркировке стоит дороже вопроса.
          </p>
          {data.notes.map((n, i) => (
            <div key={i} className="cot-note">
              ⚠ {n}
            </div>
          ))}
        </Section>
      )}

      {data.expiring.length > 0 && (
        <Section
          title={`Коды сгорают (${data.expiring.length})`}
          tone={горит > 0 ? 'alarm' : undefined}
          open
        >
          <p className="hint" style={{ marginTop: 0 }}>
            Эмитированные коды живут 30 дней. Не успели нанести — сгорают, и
            заказ придётся повторять за свои.
          </p>
          <div className="cow-expiry">
            {data.expiring.slice(0, 12).map((e) => (
              <div
                key={e.pallet_id}
                className={`cow-expiry-row${e.days_left <= 7 ? ' hot' : ''}`}
              >
                <b>{e.number}</b>
                <span className="cell-sub">{e.truck}</span>
                <span className={e.days_left < 0 ? 'cow-dead' : undefined}>
                  {e.days_left < 0 ? 'сгорели' : `осталось ${e.days_left} дн.`}
                </span>
              </div>
            ))}
            {data.expiring.length > 12 && (
              <div className="hint">…и ещё {data.expiring.length - 12}</div>
            )}
          </div>
        </Section>
      )}

      {mayEdit && (
        <Section
          title="Настройка"
          open={раскрыт === 'setup'}
          onToggle={() => setРаскрыт(раскрыт === 'setup' ? null : 'setup')}
        >
          <SourceBlock data={data} id={id} busy={busy} onError={onError} действие={действие}
          mayEdit={mayEdit}
          onBuild={onBuild}
        />
          <Nomenclature
            data={data}
            id={id}
            mayEdit={mayEdit}
            busy={busy}
            onError={onError}
            onChanged={onChanged}
            onlyCatalog
          />
          <SsccBlock
            order={data}
            busy={busy}
            onAssign={(start) => void действие(() => api.assignSscc(id, start))}
            onSetLast={(value) => void действие(() => api.setLastSscc(id, value))}
          />
          <div className="cow-sub">
            <div className="tkw-block-title">Реквизиты для агрегации</div>
            <div className="tkw-row">
              <span className="tkw-row-label">Организация</span>
              <span className="tkw-row-value">{data.org_name ?? '—'}</span>
            </div>
            <div className="tkw-row">
              <span className="tkw-row-label">ИНН</span>
              <span className="tkw-row-value">{data.org_inn ?? '—'}</span>
            </div>
            <span className="hint-inline">Берутся из карточки клиента</span>
          </div>
        </Section>
      )}
    </div>
  )
}

function Section({
  title,
  children,
  tone,
  open,
  onToggle,
}: {
  title: string
  children: React.ReactNode
  tone?: 'warn' | 'alarm'
  open?: boolean
  onToggle?: () => void
}) {
  const свёрнут = onToggle && !open
  return (
    <div className={`cow-sec${tone ? ` ${tone}` : ''}${свёрнут ? ' closed' : ''}`}>
      <button className="cow-sec-head" onClick={onToggle} disabled={!onToggle}>
        {onToggle && <span className="cot-caret">{open ? '▾' : '▸'}</span>}
        {title}
      </button>
      {!свёрнут && <div className="cow-sec-body">{children}</div>}
    </div>
  )
}

function SourceBlock({
  data,
  id,
  busy,
  mayEdit,
  onError,
  onBuild,
  действие,
}: {
  data: CodeOrderDetail
  id: number
  busy: boolean
  mayEdit: boolean
  onError: (e: string) => void
  onBuild: () => void
  действие: <T>(что: () => Promise<T>, итоги?: (r: T) => string[]) => Promise<void>
}) {
  // Пока разбор не подтверждён, поставку ещё можно пересобрать. После —
  // кнопку убираем: по дереву уже заказаны коды, и менять его молча нельзя.
  const можно_собрать = mayEdit && (data.stage === 'new' || data.stage === 'parsed')
  return (
    <div className="cow-sub">
      <div className="tkw-block-title">Состав поставки</div>
      {data.files.map((f) => (
        <button
          key={f.id}
          className="tk-file"
          onClick={() =>
            void api
              .downloadCodeOrderFile(id, f.id, f.original_name)
              .catch((e: any) => onError(e?.message ?? 'Не удалось скачать'))
          }
        >
          📊 {f.original_name}
        </button>
      ))}
      <label className="btn small tkw-file-add" style={{ cursor: 'pointer' }}>
        {busy ? 'Разбираем…' : '📥 Загрузить файл заказа'}
        <input
          type="file"
          accept=".xlsx,.xls"
          style={{ display: 'none' }}
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0]
            if (f) void действие(() => api.uploadCodeOrderSource(id, f))
          }}
        />
      </label>
      {можно_собрать && (
        <button className="btn small" disabled={busy} onClick={onBuild}>
          ✍ {data.trucks.length ? 'Пересобрать без файла' : 'Собрать без файла'}
        </button>
      )}
      {data.stage !== 'new' && data.stage !== 'parsed' && (
        <button
          className="btn small"
          disabled={busy}
          onClick={() => void действие(() => api.reopenCodeOrder(id))}
        >
          Вернуть к проверке
        </button>
      )}
      {data.confirmed_at && (
        <span className="hint-inline">Подтверждено {formatDate(data.confirmed_at)}</span>
      )}
    </div>
  )
}

/**
 * Справочник товаров и сопоставление названий.
 *
 * В файлах поставки товар зовут коротко, а в карточке Честного Знака он
 * называется совсем иначе. Эти строки не совпадут никаким сравнением, поэтому
 * программа не угадывает: один раз человек говорит, какой это товар, и дальше
 * она узнаёт его сама — в этой поставке и во всех следующих.
 */
function Nomenclature({
  data,
  id,
  mayEdit,
  busy,
  onError,
  onChanged,
  onlyCatalog,
}: {
  data: CodeOrderDetail
  id: number
  mayEdit: boolean
  busy: boolean
  onError: (e: string) => void
  onChanged: () => void
  onlyCatalog?: boolean
}) {
  const [работаем, setРаботаем] = useState(false)
  const [итог, setИтог] = useState('')

  const товары = useQuery({
    queryKey: ['code-order-products', id],
    queryFn: () => api.codeOrderProducts(id),
  })
  const список = товары.data ?? []

  const загрузить = useMutation({
    mutationFn: (file: File) => api.uploadCodeOrderCatalog(id, file),
    onSuccess: (r) => {
      setИтог(r.message)
      void товары.refetch()
      onChanged()
    },
    onError: (e: any) => onError(e?.message ?? 'Не удалось загрузить справочник'),
  })

  async function сопоставить(itemId: number, productId: number) {
    setРаботаем(true)
    try {
      await api.matchCodeOrderProduct(id, itemId, productId)
      onChanged()
    } catch (e: any) {
      onError(e?.message ?? 'Не удалось сопоставить')
    } finally {
      setРаботаем(false)
    }
  }

  const неготовых = список.filter((p) => !p.ready_for_cz).length

  if (onlyCatalog) {
    return (
      <div className="cow-sub">
        <div className="tkw-block-title">Справочник товаров</div>
        <p className="hint" style={{ marginTop: 0 }}>
          {список.length === 0
            ? 'Пуст. Загрузите выгрузку каталога Честного Знака — или любой прежний файл заказа: программа разберётся, что именно вы прислали.'
            : `Карточек: ${список.length}${
                неготовых ? `, из них ${неготовых} без состава или ТН ВЭД` : ''
              }`}
        </p>
        {итог && <p className="hint" style={{ marginTop: 0 }}>{итог}</p>}
        <label className="btn small" style={{ cursor: 'pointer' }}>
          {загрузить.isPending ? 'Читаем…' : '📚 Загрузить справочник'}
          <input
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            disabled={загрузить.isPending || busy}
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) загрузить.mutate(f)
            }}
          />
        </label>
      </div>
    )
  }

  return (
    <>
      <p className="hint" style={{ marginTop: 0 }}>
        Скажите один раз, какой это товар, — в следующих поставках программа
        узнает его сама.
      </p>
      {data.unmatched.map((u) => (
        <div key={u.item_id} className="field full">
          <label>
            {u.product}
            {u.rows > 1 && <span className="cell-sub">строк в заказе: {u.rows}</span>}
          </label>
          <select
            defaultValue=""
            disabled={работаем || busy || список.length === 0 || !mayEdit}
            onChange={(e) =>
              e.target.value && void сопоставить(u.item_id, Number(e.target.value))
            }
          >
            <option value="">
              {список.length === 0 ? '— сначала загрузите справочник —' : '— выберите товар —'}
            </option>
            {список.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name ?? p.gtin}
              </option>
            ))}
          </select>
        </div>
      ))}
    </>
  )
}

/**
 * Выдача номеров упаковок (SSCC).
 *
 * Номера идут подряд из диапазона клиента. Программа помнит, где остановилась,
 * и подставляет следующий — но правка оставлена человеку: часть диапазона
 * могли потратить мимо программы, и тогда предложенный номер уже занят.
 */
function SsccBlock({
  order,
  busy,
  onAssign,
  onSetLast,
}: {
  order: CodeOrderDetail
  busy: boolean
  onAssign: (start: string) => void
  onSetLast: (value: string) => void
}) {
  const [start, setStart] = useState('')
  const [правим, setПравим] = useState(false)
  const [последний, setПоследний] = useState('')
  const предложение = order.sscc_next ?? ''

  return (
    <div className="cow-sub">
      <div className="tkw-block-title">Номера упаковок</div>
      <p className="hint" style={{ marginTop: 0 }}>
        {order.sscc_done
          ? `Проставлены всем ${order.pallets_count} паллетам.`
          : `Паллет ${order.pallets_count}. Выдаются подряд, начиная с указанного — он тоже войдёт в серию.`}
      </p>
      <div className="field full">
        <input
          value={start}
          placeholder={предложение || 'например 00146519770140003886'}
          onChange={(e) => setStart(e.target.value)}
          inputMode="numeric"
        />
      </div>
      <button
        className="btn small primary"
        disabled={busy || !(start.trim() || предложение)}
        onClick={() => onAssign((start.trim() || предложение).replace(/[\s-]/g, ''))}
      >
        Выдать номера ({order.pallets_count})
      </button>

      <div className="hint-inline" style={{ marginTop: 8 }}>
        Последний выданный: <b>{order.sscc_last ?? 'не задан'}</b>{' '}
        <button className="link-btn" onClick={() => setПравим(!правим)}>
          {правим ? 'не менять' : 'изменить'}
        </button>
      </div>
      {правим && (
        <div className="field full">
          <input
            value={последний}
            placeholder={order.sscc_last ?? '00146519770140003879'}
            onChange={(e) => setПоследний(e.target.value)}
            inputMode="numeric"
          />
          <button
            className="btn small"
            disabled={busy || !последний.trim()}
            onClick={() => {
              onSetLast(последний.replace(/[\s-]/g, ''))
              setПравим(false)
              setПоследний('')
            }}
          >
            Запомнить
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * Декларация: файл, номер и дата.
 *
 * Отдельным окном. Номер и дата нужны, чтобы декларацию потом находили, не
 * открывая каждый PDF, — а спросить их после того, как файл уже ушёл, значит
 * не спросить вовсе.
 */
function GtdDialog({
  onClose,
  onSend,
}: {
  onClose: () => void
  onSend: (file: File, реквизиты: { number?: string; issuedOn?: string }) => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [номер, setНомер] = useState('')
  const [дата, setДата] = useState('')

  useEscClose(onClose)

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        className="modal"
        style={{ maxWidth: 520 }}
        onSubmit={(e) => {
          e.preventDefault()
          if (file) onSend(file, { number: номер.trim(), issuedOn: дата })
        }}
      >
        <h2>Декларация на товары</h2>
        <div className="modal-body">
          <div className="field full">
            <label htmlFor="gtd-num">Номер ГТД</label>
            <input
              id="gtd-num"
              value={номер}
              onChange={(e) => setНомер(e.target.value)}
              placeholder="10009100/260426/5058978"
              autoFocus
            />
          </div>
          <div className="field full">
            <label htmlFor="gtd-date">Дата ГТД</label>
            <input
              id="gtd-date"
              type="date"
              value={дата}
              onChange={(e) => setДата(e.target.value)}
            />
          </div>
          <div className="field full">
            <label>Файл</label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <span className="hint-inline">
              Только PDF — декларация приходит сканом из таможни
            </span>
          </div>
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button className="btn primary" disabled={!file}>
            Приложить
          </button>
        </div>
      </form>
    </div>
  )
}
